import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { DataGrid, GridColDef, GridSortModel, GridRenderCellParams } from '@mui/x-data-grid';
import { Box } from '@mui/material';

import Pagination, { IPagination } from 'shared/components/pagination/pagination';
import { Loader } from 'shared/components/spinner/spinner';
import { API_CONFIG } from 'shared/constants/api';
import httpService from 'shared/services/http.service';
import { debounce, getMinSecDuration } from 'shared/util/utility';
import { AddHashTagIcon, Artist, Clock, IconTikMark, Music, TableFilter } from 'shared/icons/icons';
import Button from 'shared/components/form/button';

import MusicImage from 'assets/images/music.png';
import { IMusic } from '../interface/music';
import { OrderColumn } from '../constants/music.constant';

const initPagination = {
	currentPage: 1,
	nextPage: null,
	recordPerPage: 10,
	remainingCount: 0,
	total: 0,
	totalPages: 1
};

interface IProps {
	musicSearch: string;
	activeMusicTab: string;
	isInPopUp: boolean;
	selectedRows?: IMusic[];
	isMusicList?: boolean;
	handleMusicSelection?: (row: IMusic) => void;
	setIsMusicList?: (isMusicList: boolean) => void;
	selectedArtists?: string[];
	isApply?: boolean;
}

const MusicList: FC<IProps> = (props) => {
	const {
		activeMusicTab,
		musicSearch,
		isInPopUp,
		isMusicList = false,
		handleMusicSelection,
		selectedRows = [],
		setIsMusicList,
		selectedArtists,
		isApply
	} = props;

	const [musicData, setMusicData] = useState<IMusic[]>([]);
	const [pagination, setPagination] = useState<IPagination>(initPagination);
	const [musicLoader, setMusicLoader] = useState<boolean>(false);
	const [musicOrder, setMusicOrder] = useState<{ [key: string]: string }>({ order: '', orderColumn: '' });

	const { currentPage, recordPerPage } = pagination;

	const fetchMusicList = useCallback(
		(currentPage = 1, recordPerPage = 10, search = '') => {
			const params = {
				search,
				artists: isApply ? selectedArtists : [],
				musicType: activeMusicTab.toLowerCase()
			};

			if (musicOrder.order !== '') {
				params['sort'] = musicOrder;
			}
			setMusicLoader(true);
			httpService
				.post(`${API_CONFIG.path.musicList}?page=${currentPage}&recordPerPage=${recordPerPage}`, params)
				.then((response) => {
					const { data, ...rest } = response;
					setMusicData(data);
					setMusicLoader(false);
					setPagination(rest);
					setIsMusicList(false);
				})
				.catch((error) => {
					console.error('error', error);
					setMusicLoader(false);
				});
		},
		[currentPage, recordPerPage, activeMusicTab, isApply, musicOrder]
	);

	useEffect(fetchMusicList, [activeMusicTab, musicOrder]);

	useEffect(() => {
		(isMusicList || isApply) && fetchMusicList();
	}, [isMusicList, isApply]);

	const handlePagination = (page: number, perPageVal: number) => {
		fetchMusicList(page, perPageVal);
	};

	const handleMusicSearch = useMemo(
		() =>
			debounce((value: string) => {
				fetchMusicList(1, undefined, value.trim());
			}),
		[fetchMusicList]
	);

	useEffect(() => {
		handleMusicSearch(musicSearch);
	}, [musicSearch]);

	const handleMusicOrder = (fieldName: string) => {
		setMusicOrder({
			order: musicOrder.orderColumn === fieldName && musicOrder.order === 'ASC' ? 'DESC' : 'ASC',
			orderColumn: fieldName
		});
	};

	const columns: GridColDef[] = useMemo(() => {
		if (activeMusicTab === 'library' && musicData.length > 0) {
			let libraryCols: GridColDef[] = [
				{
					field: 'title',
					headerName: 'Title',
					flex: 2,
					sortable: false,
					renderCell: (params: GridRenderCellParams<IMusic>) => (
						<div className='music-user-img'>
							<img src={(params.row.imageUrl as string) ?? MusicImage} alt='userImage' />
							<span
								className='text-truncate  text--red-hat-display font--14px font-weight-700'
								title={params.row.title as string}
							>
								{(params.row.title as string) || '-'}
							</span>
						</div>
					)
				},
				{
					field: 'artist',
					headerName: 'Artist',
					flex: 1,
					sortable: false,
					renderCell: (params: GridRenderCellParams<IMusic>) => (
						<p className='music-info-text'>{(params.row.artist as string) || '-'}</p>
					)
				},
				{
					field: 'duration',
					headerName: 'Duration',
					flex: 1,
					sortable: true,
					renderCell: (params: GridRenderCellParams) => (
						<p className='music-info-text'>
							{(params.row as any)?.duration
								? getMinSecDuration((params.row as any).duration as string)
								: '-'}
						</p>
					)
				},
				{
					field: 'linkOuts',
					headerName: 'Link Outs',
					flex: 0.8,
					sortable: true,
					renderCell: (params: GridRenderCellParams<IMusic>) =>
						params.row.linkOuts != undefined ? (params.row.linkOuts as unknown as string) : '-'
				},
				{
					field: 'plays',
					headerName: 'Plays',
					flex: 0.8,
					sortable: true,
					renderCell: (params: GridRenderCellParams<IMusic>) =>
						params.row.plays != undefined ? (params.row.plays as unknown as string) : '-'
				},
				{
					field: 'used',
					headerName: 'Used',
					flex: 0.8,
					sortable: true,
					renderCell: (params: GridRenderCellParams<IMusic>) =>
						params.row.used != undefined ? (params.row.used as unknown as string) : '-'
				}
			];

			if (isInPopUp) {
				libraryCols = [
					{
						field: 'select',
						headerName: '',
						width: 100,
						sortable: false,
						filterable: false,
						renderCell: (params: GridRenderCellParams<IMusic>) => (
							<Button
								onClick={() => handleMusicSelection && handleMusicSelection(params.row)}
								className='add-music-modal-btn'
							>
								{selectedRows.find((m: IMusic) => m.id === params.row.id) ? (
									<IconTikMark className='icon-tik' />
								) : (
									<div className='icon-plus'>
										<AddHashTagIcon />
									</div>
								)}
							</Button>
						)
					},
					...libraryCols
				];
			}

			return libraryCols;
		} else if (activeMusicTab === 'ugc' && musicData.length > 0) {
			return [
				{
					field: 'title',
					headerName: 'Title',
					flex: 2,
					sortable: false,
					renderCell: (params: GridRenderCellParams<IMusic>) => (
						<div className='music-user-img'>
							<img src={(params.row.imageUrl as string) ?? MusicImage} alt='userImage' />
							<span className='text-truncate text--red-hat-display' title={params.row.title as string}>
								{(params.row.title as string) || '-'}
							</span>
						</div>
					)
				},
				{
					field: 'userName',
					headerName: 'User',
					flex: 1,
					sortable: false,
					renderCell: (params: GridRenderCellParams) =>
						(params.row as any)?.userName ? ((params.row as any).userName as string) : '-'
				},
				{
					field: 'song',
					headerName: 'Song',
					flex: 1.2,
					sortable: false,
					renderCell: (params: GridRenderCellParams<IMusic>) =>
						params.row.title ? (params.row.title as string) : '--'
				},
				{
					field: 'artist',
					headerName: 'Artist',
					flex: 1,
					sortable: false,
					renderCell: (params: GridRenderCellParams<IMusic>) => (
						<p className='music-info-text'>{(params.row.artist as string) || '-'}</p>
					)
				},
				{
					field: 'duration',
					headerName: 'Duration',
					flex: 1,
					sortable: true,
					renderCell: (params: GridRenderCellParams) => (
						<p className='music-info-text'>
							{(params.row as any)?.duration
								? getMinSecDuration((params.row as any).duration as string)
								: '-'}
						</p>
					)
				},
				{
					field: 'plays',
					headerName: 'Plays',
					flex: 0.8,
					sortable: true,
					renderCell: (params: GridRenderCellParams<IMusic>) =>
						params.row.plays != undefined ? (params.row.plays as unknown as string) : '-'
				},
				{
					field: 'used',
					headerName: 'Used',
					flex: 0.8,
					sortable: true,
					renderCell: (params: GridRenderCellParams<IMusic>) =>
						params.row.used != undefined ? (params.row.used as unknown as string) : '-'
				}
			];
		}

		return [] as GridColDef[];
	}, [activeMusicTab, musicData, selectedRows, isInPopUp, handleMusicSelection]);

	return (
		<div className='App'>
			<Box sx={{ height: '70vh', width: '100%', background: '#fff', borderRadius: 2, boxShadow: 1 }}>
				<DataGrid
					rows={musicData}
					columns={columns}
					getRowId={(row: any) => row.id}
					rowCount={pagination.total}
					paginationMode='server'
					sortingMode='server'
					paginationModel={{ page: currentPage - 1, pageSize: recordPerPage }}
					onPaginationModelChange={(model) => handlePagination(model.page + 1, model.pageSize)}
					onSortModelChange={(model: GridSortModel) => {
						const item = model[0];
						if (!item) {
							setMusicOrder({ order: '', orderColumn: '' });
						} else {
							setMusicOrder({ order: item.sort === 'asc' ? 'ASC' : 'DESC', orderColumn: item.field });
						}
					}}
					loading={musicLoader}
					disableRowSelectionOnClick
					sx={{
						'& .MuiDataGrid-columnHeaders': {
							backgroundColor: '#f3f4f6',
							fontWeight: 600
						}
					}}
				/>
			</Box>
			{pagination.total > 0 && !musicLoader && (
				<Pagination
					handlePageClick={(page: number) => handlePagination(page, recordPerPage)}
					handlePageSelection={(perPage: number) => handlePagination(currentPage, perPage)}
					loading={musicLoader}
					pagination={pagination}
				/>
			)}
		</div>
	);
};

export default MusicList;
